import axios from "axios";
import config from "@Config/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useEffect, useMemo, useState } from "react";

export type CmsOffer = {
  offer?: {
    label?: string;
    title?: string;
    subtitle?: string;
    redirectLink?: string;
    image?: {
      url?: string;
      alt?: string;
    };
    partner?: {
      logo?: {
        url?: string;
        alt?: string;
      };
    };
  };
  heroImage?: {
    url?: string;
    alt?: string;
  };
};

let cachedOffers: CmsOffer[] | null = null;
let offersRequest: Promise<CmsOffer[]> | null = null;

// Offer card width plus its 16px trailing gap (.offer-card margin-right). Drives the
// carousel arrow step and the scroll-depth maths wherever these cards are railed.
export const CARD_STRIDE_DESKTOP = 372;
export const CARD_STRIDE_MOBILE = 330;

export const PRIMARY_AMBER_PLUS_BANNER = {
  href: "/plus?utm_source=home_page",
  desktopBg: `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/Offer-Card-03.png?q=90`,
  mobileBg: `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/refer3.png?q=90`,
};

const getCmsUrl = (path: string) => `${config.CMS_API_URL}${path}`;

export const getPartnerName = (label = "") => label.split("—")[0].trim() || "Amber+";
export const getOfferCategory = (label = "") => label.split("—")[1]?.trim() || "General";
export const getPartnerLogo = (item: CmsOffer) =>
  item.offer?.partner?.logo || item.offer?.image || { url: "", alt: "" };

const fetchAmberPlusOffers = async (): Promise<CmsOffer[]> => {
  if (cachedOffers) return cachedOffers;
  if (offersRequest) return offersRequest;

  offersRequest = axios
    .get(getCmsUrl("/api/amberplus-homepage-featured?depth=3&draft=false"))
    .then((response) => {
      const items = response?.data?.docs?.[0]?.items || [];
      cachedOffers = items;
      return items;
    })
    .catch(() => [])
    .finally(() => {
      offersRequest = null;
    });

  return offersRequest;
};

/**
 * CMS-backed Amber+ partner offers, filtered to the renderable ones. Shared by the merged
 * home rail (ReferralProgram) and the standalone Payment Success section, so a host can
 * size its own carousel analytics before the cards mount. The request is module-cached.
 */
export const useAmberPlusOffers = (): CmsOffer[] => {
  const [offers, setOffers] = useState<CmsOffer[]>([]);

  useEffect(() => {
    let active = true;
    const fetchOffers = async () => {
      const items = await fetchAmberPlusOffers();
      if (active) setOffers(items);
    };

    fetchOffers();
    return () => {
      active = false;
    };
  }, []);

  return useMemo(
    () => offers.filter((item) => Boolean(item.heroImage?.url && item.offer?.redirectLink)),
    [offers],
  );
};

export const emitOfferClick = (offerName: string, offerIndex: number, offerCategory: string) => {
  gtagEventEmitter({
    action: "amber_plus_offer_clicked",
    offer_name: offerName,
    offer_index: offerIndex,
    offer_category: offerCategory,
  });
};
